import { getModule as getStoreModule } from 'vuex-module-decorators';
import ISlideMaster from '@app/types/presentations/master/ISlideMaster';
import ISlideView from '@app/types/ISlideView';
import DummySlideTransform from '@app/transforms/slides/DummySlideTransform';
import WelcomeSlideTransform from '@app/transforms/slides/WelcomeSlideTransform';
import IPresentationSettings from '@app/types/presentations/presentation/IPresentationSettings';
import ISlideTransform from '@app/types/ISlideTransform';
import HighlightSlideTransform from '@app/transforms/slides/HighlightSlideTransform';
import OfferASlideTransform from '@app/transforms/slides/OfferASlideTransform';
import OfferBSlideTransform from '@app/transforms/slides/OfferBSlideTransform';
import OfferCSlideTransform from '@app/transforms/slides/OfferCSlideTransform';
import OfferDSlideSlideTransform from '@app/transforms/slides/OfferDSlideTransform';
import OfferESlideTransform from '@app/transforms/slides/OfferESlideTransform';
import ThankYouSlideTransform from '@app/transforms/slides/ThankYouSlideTransform';
import ChapterOverviewSlideSlideTransform from '@app/transforms/slides/ChapterOverviewSlideTransform';
import HomeSlideTransform from '@app/transforms/slides/HomeSlideTransform';
import CustomSlideTransform from '@app/transforms/slides/CustomSlideTransform';
import SlideType from '@app/types/presentations/SlideType';
import MaskedOverviewSlideTransform from '@app/transforms/slides/MaskedOverviewSlideTransform';
import TravelPassSlideTransform from '@app/transforms/slides/TravelPassSlideTransform';
import TravelPassVipSlideTransform from '@app/transforms/slides/TravelPassVipSlideTransform';
import SingleMediaSlideTransform from '@app/transforms/slides/SingleMediaSlideTransform';
import CooperationSlideTransform from '@app/transforms/slides/CooperationSlideTransform';
import TrainRouteSlideTransform from '@app/transforms/slides/TrainRouteSlideTransform';
import RoutePricesSlideTransform from '@app/transforms/slides/RoutePricesSlideTransform';
import SkiRegionSlideTransform from '@app/transforms/slides/SkiRegionSlideTransform';
import GlobalsStoreModule from '@app/store/modules/GlobalsStoreModule';
import AppStoreModule from '../store/modules/AppStoreModule';

const contentTypeTransformMap: Record<SlideType, { new (): ISlideTransform }> = {
	welcomeSlide: WelcomeSlideTransform,
	homeSlide: HomeSlideTransform,
	thankYouSlide: ThankYouSlideTransform,
	highlightSlide: HighlightSlideTransform,
	chapterOverviewSlide: ChapterOverviewSlideSlideTransform,
	maskedOverviewSlide: MaskedOverviewSlideTransform,
	travelPassSlide: TravelPassSlideTransform,
	travelPassVipSlide: TravelPassVipSlideTransform,
	offerASlide: OfferASlideTransform,
	offerBSlide: OfferBSlideTransform,
	offerCSlide: OfferCSlideTransform,
	offerDSlide: OfferDSlideSlideTransform,
	offerESlide: OfferESlideTransform,
	customSlide: CustomSlideTransform,
	cooperationSlide: CooperationSlideTransform,
	singleMediaSlide: SingleMediaSlideTransform,
	trainRouteSlide: TrainRouteSlideTransform,
	routePricesSlide: RoutePricesSlideTransform,
	skiRegionSlide: SkiRegionSlideTransform
};

const globalsStore: GlobalsStoreModule = getStoreModule(GlobalsStoreModule);

const cachedTransforms: Record<string, ISlideView> = {};

/**
 * Transforms a slide as stored in the store state including a mapping to a slide view component and its properties,
 * ready to be used for Present.vue and the slide views
 */
export default async function transformSlide(
	slide: ISlideMaster,
	presentationSettings: IPresentationSettings
): Promise<ISlideView> {
	const cacheKey: string = `${JSON.stringify({ slide, presentationSettings })}-${globalsStore.entitiesDateUpdated}`;

	if (!cachedTransforms[cacheKey]) {
		const transform: ISlideTransform = contentTypeTransformMap[slide.type as SlideType]
			? new contentTypeTransformMap[slide.type as SlideType]()
			: new DummySlideTransform();

		try {
			cachedTransforms[cacheKey] = {
				viewComponent: transform.viewComponent,
				data: await transform.transform(slide, presentationSettings)
			};
		} catch (error) {
			// handle errors during transform and resolve to an error slide instead
			const appStore: AppStoreModule = getStoreModule(AppStoreModule);
			appStore.captureError(error);

			cachedTransforms[cacheKey] = {
				viewComponent: 'ErrorSlide',
				data: await transform.handleTransformError(slide, error)
			};
		}
	}

	return cachedTransforms[cacheKey];
}
